import {
  Card,
  Chip,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  ChatBubbleOutlineRounded,
  CheckBoxOutlined,
  DeleteRounded,
  EditRounded,
  LockRounded,
  MoreVertRounded,
  NotInterestedRounded,
  PublicRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  getWorkflowStepsMasterDataAsync,
  WorkflowStep
} from './workflowStepSlice';
import clsx from 'clsx';

interface WorkflowStepParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    width70: {
      width: '70%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    width30: {
      width: '30%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    chip: {
      margin: theme.spacing(0, 0.5, 0, -1)
    },
    icon: {
      marginLeft: theme.spacing(2.5),
      '& > *': {
        marginRight: theme.spacing(1)
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    },
    greenColor: {
      color: theme.palette.success.main
    }
  })
);

export default function WorkflowStepView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const sortOptions = [
    { value: 'id', text: t('workflowStepView.sortOptions.createdDate') },
    { value: 'name', text: t('workflowStepView.sortOptions.name') },
    { value: 'order', text: t('workflowStepView.sortOptions.order') }
  ];

  const { masterData: workflowSteps, count } = useSelector(
    (state: RootState) => state.workflowSteps
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedWorkflowStep, setSelectedWorkflowStep] =
    useState<WorkflowStep | null>(null);
  const [workflowStepParams, setWorkflowStepParams] =
    useState<WorkflowStepParamProps>({
      page: 0,
      limit: 30,
      sortBy: 'order',
      orderBy: ORDER.asc
    });

  useEffect(() => {
    loadingWorkflowStep(workflowStepParams);
  }, []);

  function loadingWorkflowStep(params: WorkflowStepParamProps) {
    dispatch(getWorkflowStepsMasterDataAsync(params));
    setWorkflowStepParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingWorkflowStep({
      ...workflowStepParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, workflowStep?: WorkflowStep) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && workflowStep) {
        setSelectedWorkflowStep(workflowStep);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedWorkflowStep?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`workflow-steps/${selectedWorkflowStep?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('workflowStepView.drawer.editWorkflowStep')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('workflowStepView.drawer.deleteWorkflowStep')}
                secondary={t('workflowStepView.drawer.deleteFunctionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('workflowStepView.breadcrumb.workflowStep'),
          masterDataRef: 'workflow-steps'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('workflowStepView.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('workflowStepView.toolbar.name') as string}
        params={workflowStepParams}
        sortOptions={sortOptions}
        loadingElement={loadingWorkflowStep}
      />
      {workflowSteps.map((workflowStep) => {
        return (
          <Card className={classes.card} key={workflowStep.id}>
            <div className={classes.cardContainer}>
              <div className={classes.width70}>
                <Typography>
                  <Chip
                    className={classes.chip}
                    size="small"
                    variant="outlined"
                    label={workflowStep.order}
                  />
                  {workflowStep.name}
                </Typography>
              </div>
              <div className={clsx(classes.width30, classes.icon)}>
                {workflowStep.isInternal ? (
                  <Tooltip title={t('workflowStepView.tooltips.encOnly') as string} arrow>
                    <LockRounded color="action" />
                  </Tooltip>
                ) : (
                  <Tooltip title={t('workflowStepView.tooltips.public') as string} arrow>
                    <PublicRounded color="action" />
                  </Tooltip>
                )}
                {workflowStep.canStopWorkflow && (
                  <Tooltip title={t('workflowStepView.tooltips.canStopWorkflow') as string} arrow>
                    <NotInterestedRounded color="error" />
                  </Tooltip>
                )}
                {workflowStep.isFinalApprover && (
                  <Tooltip title={t('workflowStepView.tooltips.finalApprover') as string} arrow>
                    <ChatBubbleOutlineRounded color="primary" />
                  </Tooltip>
                )}
                {workflowStep.isChecklist && (
                  <Tooltip title={t('workflowStepView.tooltips.qualityCheck') as string} arrow>
                    <CheckBoxOutlined className={classes.greenColor} />
                  </Tooltip>
                )}
              </div>
            </div>
            <div>
              <IconButton
                size="small"
                onClick={toggleDrawer(true, workflowStep)}
              >
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / workflowStepParams.limit)}
          shape="rounded"
          page={workflowStepParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`workflow-steps/create`}
          variant="extended"
          color="primary"
          aria-label={t('workflowStepView.fab.ariaLabel') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('workflowStepView.fab.newWorkflowStep')}
        </Fab>
      </Zoom>
    </div>
  );
}
